import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { fetchAssignmentById, fetchQuestionsForAssignment } from '../pages/services/assignmentService';
import AssignmentQuestionPanel from './ReusableComponent/AssignmentQuestionPanel';
import { useHighlightCode } from './utils/utility';
import "../styles/AssignmentDetails.css";
import "./ReusableComponent/AssignmentQuestionPanel.css";

const BUDDY_BASE = "https://buddy.myskillverse.ai/assignment.html";

const stripHtml = (html) => {
  if (!html) return "—";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "—";
};

const DIFFICULTY_CLASS = {
  Easy: "aq-easy", Medium: "aq-medium", Hard: "aq-hard",
  "Very Hard": "aq-very-hard", Professional: "aq-professional",
};

const AssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const pageRef = useRef(null);
  useHighlightCode(pageRef, [assignment]);

  // Questions state
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const load = async () => {
      const delay = new Promise(resolve => setTimeout(resolve, 2000));
      try {
        const [data] = await Promise.all([fetchAssignmentById(id), delay]);
        setAssignment(data);

        // Extract question IDs from the assignment response
        const existingIds = Array.isArray(data.questionIds) ? data.questionIds
          : Array.isArray(data.questions) && typeof data.questions[0] === "string" ? data.questions
          : Array.isArray(data.questions) && data.questions.length && data.questions[0]?._id
            ? data.questions.map(q => q._id)
          : [];

        if (existingIds.length > 0) {
          setQuestionsLoading(true);
          const qObjs = await fetchQuestionsForAssignment(existingIds);
          setQuestions(qObjs);
          setQuestionsLoading(false);
        }
      } catch (err) {
        setError('Error fetching assignment details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="overlay">
        <div className="custom-loader" />
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  if (error || !assignment) {
    return <p className="error-text">{error || 'No assignment data found'}</p>;
  }

  const batch = assignment.batch || {};

  const handleEdit = () => navigate(`/assignments/${id}/update`);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${BUDDY_BASE}?id=${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSliderConfirm = async (selectedIds, _removedIds, objects) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/assignments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...buildUpdatePayload(), questionIds: selectedIds }),
      });
      if (!res.ok) throw new Error("Failed to update questions");
      setQuestions(objects);
    } catch (err) {
      console.error("Error updating questions:", err);
    } finally {
      setSaving(false);
    }
  };

  const buildUpdatePayload = () => ({
    assignmentTitle: assignment.assignmentTitle,
    assignmentDuration: assignment.assignmentDuration,
    assignmentDescription: assignment.assignmentDescription,
    batchId: batch._id,
    assignmentTime: assignment.assignmentTime,
    assignmentAnalysisTime: assignment.assignmentAnalysisTime,
    assignmentAttachment: assignment.assignmentAttachment,
    status: assignment.status,
  });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const updatedIds = questions.filter(q => q._id !== deleteTarget).map(q => q._id);
      const res = await fetch(`${API_URL}/api/assignments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...buildUpdatePayload(), questionIds: updatedIds }),
      });
      if (!res.ok) throw new Error("Failed to remove question");
      setQuestions(prev => prev.filter(q => q._id !== deleteTarget));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error removing question:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="assignment-details-container" ref={pageRef}>

      {deleteTarget && (
        <div className="aq-del-overlay">
          <div className="aq-del-modal">
            <p>Remove this question from the assignment?</p>
            <div className="aq-del-actions">
              <button className="aq-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="aq-confirm-del-btn" onClick={confirmDelete} disabled={saving}>
                {saving ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AssignmentQuestionPanel
        open={sliderOpen}
        onClose={() => setSliderOpen(false)}
        onConfirm={handleSliderConfirm}
        existingIds={questions.map(q => q._id)}
        existingObjects={questions}
      />

      <div className="assignment-header">
        <h1>{assignment.assignmentTitle}</h1>
        <div className="assignment-header-actions">
          <div className="ad-copy-wrap">
            <button className="ad-link-btn" title="Copy assignment link" onClick={handleCopyLink}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </button>
            {copied && <span className="ad-copy-tip">Copied!</span>}
          </div>
          <button className="edit-btn" onClick={handleEdit}>Edit</button>
        </div>
      </div>

      <div className="details-assignment-layout">
        {/* Right — Batch */}
        <div className="right-batch-section">
          <div className="batch-card">
            <h3>Batch Details</h3>
            {batch?.batchTitle ? (
              <>
                <p><strong>Batch Title:</strong> {batch.batchTitle}</p>
                <p><strong>Batch Duration:</strong> {batch.batchDuration}</p>
                <p><strong>Batch Description:</strong> {batch.batchDescription}</p>
                <p><strong>Batch Time:</strong> {batch.batchTime}</p>
                <p><strong>Batch Days:</strong> {Array.isArray(batch.batchDays) ? batch.batchDays.join(', ') : 'N/A'}</p>
                <p><strong>Batch Status:</strong> {batch.batchStatus}</p>
              </>
            ) : (
              <p>No Batch available</p>
            )}
          </div>
        </div>

        {/* Left — Assignment details + Questions */}
        <div className="left-assignment-section">
          <div className="card-assignment-block">
            <h3>Assignment Details</h3>
            <table className="assign-details-table">
              <tbody>
                <tr><td>Assignment Title:</td><td>{assignment.assignmentTitle}</td></tr>
                <tr><td>Assignment Duration:</td><td>{assignment.assignmentDuration}</td></tr>
                <tr>
                  <td style={{ verticalAlign: "top" }}>Assignment Description:</td>
                  <td>
                    <div className="ad-rich-content" dangerouslySetInnerHTML={{ __html: assignment.assignmentDescription || "—" }} />
                  </td>
                </tr>
                <tr><td>Assignment Time:</td><td>{assignment.assignmentTime}</td></tr>
                <tr><td>Assignment Analysis Time:</td><td>{assignment.assignmentAnalysisTime}</td></tr>
                <tr>
                  <td>Assignment Attachment:</td>
                  <td>
                    {assignment.assignmentAttachment ? (
                      <a href={assignment.assignmentAttachment} target="_blank" rel="noopener noreferrer"
                        style={{ textDecoration: 'underline', fontSize: '0.9rem', display: 'inline-block', marginTop: '4px' }}>
                        {assignment.assignmentAttachment}
                      </a>
                    ) : 'N/A'}
                  </td>
                </tr>
                <tr><td>Assignment Status:</td><td>{assignment.status}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Questions section */}
          <div className="card-assignment-block">
            <div className="aq-section-header">
              <h3 style={{margin:0}}>
                Questions
                <span style={{fontWeight:400,color:"#9ca3af",fontSize:"13px",marginLeft:8}}>
                  ({questions.length})
                </span>
              </h3>
              <button className="aq-add-btn" onClick={() => setSliderOpen(true)} disabled={saving}>
                + Add Questions
              </button>
            </div>

            <div style={{marginTop:14}}>
              {questionsLoading ? (
                <p style={{color:"#9ca3af",fontSize:"14px"}}>Loading questions…</p>
              ) : questions.length === 0 ? (
                <div className="aq-empty">No questions attached to this assignment yet.</div>
              ) : (
                <div className="aq-table-wrap">
                  <table className="aq-table">
                    <thead>
                      <tr>
                        <th className="aq-idx">#</th>
                        <th>Question</th>
                        <th>Type</th>
                        <th>Difficulty</th>
                        <th>Topic</th>
                        <th>Subject</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((q, i) => (
                        <tr key={q._id}>
                          <td className="aq-idx">{i + 1}</td>
                          <td><div className="aq-q-text" title={stripHtml(q.question)}>{stripHtml(q.question)}</div></td>
                          <td>{q.questionType}</td>
                          <td>
                            <span className={`aq-badge ${DIFFICULTY_CLASS[q.difficultyLevel] || ""}`}>
                              {q.difficultyLevel}
                            </span>
                          </td>
                          <td>{q.topic}</td>
                          <td>{q.subject}</td>
                          <td>
                            <button className="aq-del-btn" onClick={() => setDeleteTarget(q._id)} disabled={saving}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AssignmentDetails;
